import {
  Checkbox,
  UpdateIconButton,
  LinearProgress,
  ListMenu,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  pn,
} from "@compass/core";

import { ListBeProperty } from "components";
import { DeveloperListDto, DeveloperListResource, Permission, PermissionAction, SortObject } from "generated/openApi";
import { useNavigate } from "react-router-dom";
import { isAuthorized } from "services";

import { developerPageRoute } from "pages/cobro/developers/DeveloperPage/DeveloperPage";
import { useDeveloperListMenuItems } from "./useDeveloperListMenuItems";
import { useDateTime } from "hooks";

export const DeveloperTable = (props: {
  rootPermission: Permission;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  developers: DeveloperListDto[];
  listTotal: number | undefined;
  resource: DeveloperListResource;
  searchFocused: boolean;
  sortObjects: SortObject[] | undefined;
  sort: (sortObjects: SortObject[]) => void;
  selectId: (id: number) => void;
  selectedIds: number[];
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
  getIdsPromise: () => Promise<number[]>;
  loadList: () => void;
}) => {
  const navigate = useNavigate();
  const properties = props.resource.properties || [];
  const menuItems = useDeveloperListMenuItems({ permission: props.rootPermission });
  const formatDateTime = useDateTime();

  return (
    <TableContainer ref={props.tableContainerRef}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {menuItems.length > 0 && (
              <TableCell type="select">
                <ListMenu
                  selectedIds={props.selectedIds}
                  setSelectedIds={props.setSelectedIds}
                  listTotal={props.listTotal}
                  fetchFunction={() => props.getIdsPromise()}
                  actionMenuItems={menuItems}
                />
              </TableCell>
            )}
            <TableCell type="id">
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<DeveloperListDto>("id"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>

            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<DeveloperListDto>("createdDate"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<DeveloperListDto>("name"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<DeveloperListDto>("gender"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<DeveloperListDto>("developerType"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.developers.map((developerItem) => (
            <TableRow
              key={developerItem.id}
              onClick={() => navigate(developerPageRoute.getPath({ id: developerItem.id, crudMode: "view" }))}
            >
              {menuItems.length > 0 && (
                <TableCell type="select">
                  <Checkbox
                    checked={props.selectedIds.includes(developerItem.id)}
                    onClick={(e) => props.selectId(developerItem.id)}
                  />
                </TableCell>
              )}
              <TableCell type="id">{developerItem.id}</TableCell>
              <TableCell>{formatDateTime(developerItem.createdDate)}</TableCell>
              <TableCell>{developerItem.name}</TableCell>
              <TableCell>{developerItem.gender}</TableCell>
              <TableCell>{developerItem.developerType}</TableCell>
              <TableCell></TableCell>
              <TableCell>
                {developerItem.progressPercent && <LinearProgress value={developerItem.progressPercent} />}
              </TableCell>
              <TableCell type="action">
                {isAuthorized(props.rootPermission, PermissionAction.Update) && (
                  <UpdateIconButton color="primary"
                    onClick={() => navigate(developerPageRoute.getPath({ id: developerItem.id, crudMode: "edit" }))}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
